import React, { Fragment } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { SITENAMEALIAS } from "../utils/init";
import { Link } from "react-router-dom";
import { showToast, showHttpError, currentPageAccess } from "../utils/library";
import {
  FetchMerchantStatus,
  ResendEmailConfirm,
  getAuthToken,
  getAuthUuid,
  getMerchantCardApplications,
} from "../utils/service";
import GetStartedNew from "./GetStartedNew";

const styles = {
  pageWrapper: {
    padding: "40px 24px 60px",
  },
  welcomeSection: {
    textAlign: "center",
    marginBottom: "36px",
  },
  welcomeTitle: {
    fontSize: "26px",
    fontWeight: "700",
    marginBottom: "6px",
    color: "var(--text-color)",
  },
  welcomeSubtitle: {
    fontSize: "15px",
    opacity: 0.7,
    margin: 0,
  },
  progressTrack: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "28px auto 40px",
    maxWidth: "440px",
  },
  stepCircle: (done, isCurrentStep) => ({
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: done
      ? "#10b981"
      : isCurrentStep
      ? "var(--btn-color)"
      : "var(--body-color-less2)",
    color: done || isCurrentStep ? "#fff" : "var(--text-color)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "700",
    fontSize: "14px",
    flexShrink: 0,
    transition: "all 0.4s ease",
    boxShadow: isCurrentStep
      ? "0 0 0 4px color-mix(in srgb, var(--btn-color), transparent 70%)"
      : "none",
  }),
  stepLabel: (done, isCurrentStep) => ({
    fontSize: "11px",
    marginTop: "8px",
    color: done
      ? "#10b981"
      : isCurrentStep
      ? "var(--btn-color)"
      : "var(--text-color)",
    fontWeight: done || isCurrentStep ? "600" : "400",
    whiteSpace: "nowrap",
    opacity: done || isCurrentStep ? 1 : 0.5,
  }),
  connector: (done) => ({
    flex: 1,
    height: "3px",
    background: done ? "#10b981" : "var(--body-color-less2)",
    marginBottom: "26px",
    marginLeft: "4px",
    marginRight: "4px",
    borderRadius: "2px",
    transition: "background 0.4s ease",
  }),
  cardsRow: {
    display: "flex",
    gap: "24px",
    alignItems: "stretch",
    justifyContent: "center",
  },
  card: (isActive, _isCompleted, isDisabled) => ({
    flex: isActive ? "1.18 1 0%" : "1 1 0%",
    display: "flex",
    flexDirection: "column",
    background: "var(--body-color-less)",
    borderRadius: "16px",
    overflow: "visible",
    position: "relative",
    transition: "all 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    boxShadow: isActive
      ? "0 16px 48px rgba(0, 82, 255, 0.22), 0 0 0 2px var(--btn-color)"
      : "0 2px 12px rgba(0,0,0,0.06)",
    opacity: isDisabled ? 0.45 : 1,
    zIndex: isActive ? 2 : 1,
    border: isActive ? "none" : "1px solid var(--body-color-less2)",
    pointerEvents: isDisabled ? "none" : "auto",
    marginTop: isActive ? "-12px" : "0",
    marginBottom: isActive ? "-12px" : "0",
    paddingTop: isActive ? "12px" : "0",
    paddingBottom: isActive ? "12px" : "0",
    alignSelf: isActive ? "stretch" : "stretch",
    minHeight: isActive ? "calc(100% + 24px)" : "auto",
  }),
  badge: {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "var(--btn-color)",
    color: "#fff",
    fontSize: "10px",
    fontWeight: "700",
    padding: "4px 14px",
    borderRadius: "20px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    zIndex: 3,
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(0, 82, 255, 0.3)",
  },
  completedBadge: {
    position: "absolute",
    top: "-12px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#10b981",
    color: "#fff",
    fontSize: "10px",
    fontWeight: "700",
    padding: "4px 14px",
    borderRadius: "20px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    zIndex: 3,
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
  },
  iconArea: (isActive, isCompleted) => ({
    height: "110px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "16px 16px 0 0",
    background: isActive
      ? "linear-gradient(135deg, color-mix(in srgb, var(--btn-color), transparent 88%), color-mix(in srgb, var(--btn-color), transparent 95%))"
      : isCompleted
      ? "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.03))"
      : "transparent",
    transition: "background 0.4s ease",
  }),
  icon: (isActive, isCompleted) => ({
    fontSize: "48px",
    color: isActive
      ? "var(--btn-color)"
      : isCompleted
      ? "#10b981"
      : "var(--text-color)",
    opacity: isActive || isCompleted ? 1 : 0.4,
    transition: "all 0.4s ease",
  }),
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "20px 24px 24px",
    textAlign: "center",
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "700",
    marginBottom: "8px",
  },
  cardDesc: {
    fontSize: "13px",
    opacity: 0.65,
    lineHeight: "1.5",
    flex: 1,
    marginBottom: "18px",
  },
  btnPrimary: {
    display: "inline-block",
    padding: "10px 28px",
    background: "var(--btn-color)",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s ease",
  },
  btnOutline: {
    display: "inline-block",
    padding: "10px 28px",
    background: "transparent",
    color: "var(--btn-color)",
    border: "2px solid var(--btn-color)",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
    transition: "all 0.2s ease",
  },
  btnDisabled: {
    display: "inline-block",
    padding: "10px 28px",
    background: "var(--body-color-less2)",
    color: "var(--text-color)",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "not-allowed",
    opacity: 0.5,
  },
  stepNumber: {
    position: "absolute",
    top: "12px",
    left: "16px",
    fontSize: "12px",
    fontWeight: "700",
    opacity: 0.3,
  },
};

// On https://trade.paybito.com we require localStorage credentials
// (access_token, brokerId, uuid). Helper used by GetStarted to decide
// whether to silently redirect to /login on mount.
const isTradePaybitoMissingCreds = () => {
  if (window.location.origin !== "https://trade.paybito.com") return false;
  return (
    !getAuthToken() ||
    !localStorage.getItem(SITENAMEALIAS + "_brokerId") ||
    !getAuthUuid()
  );
};

class GetStarted extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showLoader: false,
      pageNo: 1,
      noOfItemsPerPage: 20,
      isEmailConfirmed: localStorage.getItem(
        SITENAMEALIAS + "_email_confirmed"
      ),
      isVerificationSubmitted: localStorage.getItem(
        SITENAMEALIAS + "_basic_verification_submitted"
      ),
      isCryptoAddressAdded: localStorage.getItem(
        SITENAMEALIAS + "_crypto_address_added"
      ),
      accountStatusId: localStorage.getItem(
        SITENAMEALIAS + "_account_status_id"
      ),
      isEmailResend: false,
      permission: "",
      shouldSkipRender: isTradePaybitoMissingCreds(),
      cardApplications: [],
    };
  }

  handleMerchantStatus = () => {
    this.setState({ showLoader: true });
    // Sub-merchants should see their parent merchant's verification details,
    // since onboarding/verification status lives on the parent account.
    const parentMerchantId = localStorage.getItem(
      SITENAMEALIAS + "_parent_merchant_id"
    );
    const isSubMerchant = parentMerchantId != "0";
    console.log(
      "Fetching merchant status with merchant_id:",
      isSubMerchant,
      parentMerchantId
    );
    let payload = {
      merchant_id: isSubMerchant
        ? parentMerchantId
        : localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    FetchMerchantStatus(payload)
      .then(
        function (res) {
          this.setState({ showLoader: false });
          var response = res.data[0];
          if (response.error != "0") {
            showToast("error", response.error_msg);
          } else {
            localStorage.setItem(
              SITENAMEALIAS + "_email_confirmed",
              response["email_confirmed"]
            );
            localStorage.setItem(
              SITENAMEALIAS + "_basic_verification_submitted",
              response["basic_verification_submitted"]
            );
            localStorage.setItem(
              SITENAMEALIAS + "_crypto_address_added",
              response["crypto_address_added"]
            );
            localStorage.setItem(
              SITENAMEALIAS + "_account_status_id",
              response["account_status_id"]
            );

            this.setState({
              isEmailConfirmed: response["email_confirmed"],
              isVerificationSubmitted: response["basic_verification_submitted"],
              isCryptoAddressAdded: response["crypto_address_added"],
              accountStatusId: response["account_status_id"],
            });
          }
        }.bind(this)
      )
      .catch(
        function (err) {
          this.setState({ showLoader: false });
          showHttpError(err);
        }.bind(this)
      );
  };

  handleResendEmail = () => {
    this.setState({ showLoader: true });
    let payload = {
      merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    ResendEmailConfirm(payload)
      .then(
        function (res) {
          this.setState({ showLoader: false });
          var response = res.data[0];
          if (response.error != "0") {
            showToast("error", response.error_msg);
          } else {
            this.setState({
              isEmailResend: true,
            });
          }
        }.bind(this)
      )
      .catch(
        function (err) {
          this.setState({ showLoader: false });
          showHttpError(err);
        }.bind(this)
      );
  };

  handleUserStatus = () => {
    if (
      this.state.isCryptoAddressAdded == "1" &&
      this.state.isEmailConfirmed == "1" &&
      this.state.isVerificationSubmitted == "1"
    ) {
      return "Your account has been fully activated.";
    }
    return "Let's get you ready to accept payments.";
  };

  getCompletedSteps = () => {
    return {
      step1: this.state.isEmailConfirmed == "1",
      step2: this.state.isVerificationSubmitted == "1",
      step3: this.state.isCryptoAddressAdded == "1",
    };
  };

  getActiveStep = () => {
    if (this.state.isEmailConfirmed != "1") return 1;
    if (this.state.isVerificationSubmitted != "1") return 2;
    if (this.state.isCryptoAddressAdded != "1") return 3;
    return 0;
  };

  startTimerForEmailOtp = (i) => {
    let count = i;

    this.setState({ timerTxtEmail: count }, () => {
      let downloadTimer = setInterval(() => {
        if (count <= 1) {
          clearInterval(downloadTimer);
          this.setState({
            secondStatusCheckForEmailOtp: false,
          });
          localStorage.setItem("secondStatusCheckForEmailOtp", "false");
          this.state.isTimerStartEmailGetCode = false;
        }
        count -= 1;
        localStorage.setItem("cdforemail", count);
        this.setState({ timerTxtEmail: localStorage.getItem("cdforemail") });
      }, 1000);
    });
  };

  componentDidMount() {
    // On trade.paybito.com without localStorage creds, silently redirect
    // to /login. Skip all API calls and state setup so nothing flashes.
    if (this.state.shouldSkipRender) {
      // Defensive cleanup: any partial cred should be cleared so the
      // Login page does not bounce back to /autologin in a loop.
      localStorage.removeItem(SITENAMEALIAS + "_access_token");
      localStorage.removeItem(SITENAMEALIAS + "_brokerId");
      localStorage.removeItem(SITENAMEALIAS + "_uuid");
      if (this.props.history) {
        this.props.history.replace("/");
      } else {
        window.location.replace("/");
      }
      return;
    }

    this.setState({
      permission: currentPageAccess(),
    });

    if (localStorage.getItem("secondStatusCheckForEmailOtp") == "true") {
      this.startTimerForEmailOtp(localStorage.getItem("cdforemail"));
      this.setState({
        secondStatusCheckForEmailOtp: true,
        isTimerStartEmailGetCode: true,
        timerTxtEmail: localStorage.getItem("cdforemail"),
        loginBtn: false,
      });
    } else {
      this.setState({
        secondStatusCheckForEmailOtp: false,
      });
    }
    this.handleMerchantStatus();
    this.fetchCardApplications();
  }

  fetchCardApplications = () => {
    const parentMerchantId = localStorage.getItem(SITENAMEALIAS + "_parent_merchant_id");
    const isSubMerchant = parentMerchantId != "0" && parentMerchantId != null;
    let merchantId = isSubMerchant
      ? parentMerchantId
      : localStorage.getItem(SITENAMEALIAS + "_merchant_id");

    if (!merchantId) return;

    // Optional: getAuthUuid is still passed just in case it's needed by the service method internally, but we don't strictly block on it if it's not strictly required in the URL.
    getMerchantCardApplications(merchantId, getAuthUuid() || "")
      .then((res) => {
        if (res.data && res.data.error === "0") {
          this.setState({ cardApplications: res.data.card_applications || [] });
        }
      })
      .catch((err) => {
        console.error("Error fetching card applications:", err);
      });
  };

  render() {
    // Skip render entirely while we redirect off trade.paybito.com.
    if (this.state.shouldSkipRender) {
      return null;
    }

    // After business activation, show the post-verification design
    if (this.state.accountStatusId == "6") {
      return <GetStartedNew />;
    }

    const steps = this.getCompletedSteps();
    const activeStep = this.getActiveStep();
    const stepDone = [steps.step1, steps.step2, steps.step3];
    const stepLabels = [
      "Confirm Email",
      "Business Activation",
      "Accept Payment",
    ];
    const allComplete = activeStep === 0;

    return (
      <Fragment>
        <style>{`
          @media (max-width: 576px) {
            .gs-progress-track { max-width: 100% !important; padding: 0 8px; }
            .gs-step-label { font-size: 9px !important; white-space: normal !important; text-align: center; max-width: 70px; }
            .gs-cards-row { flex-direction: column !important; }
            .gs-welcome-title { font-size: 20px !important; }
          }
        `}</style>
        <div className="container-scroller">
          <Sidebar />
          <Header />
          <div className="main-panel">
            <div style={styles.pageWrapper}>
              {/* Welcome */}
              <div style={styles.welcomeSection}>
                <h2 className="gs-welcome-title" style={styles.welcomeTitle}>
                  Welcome to {localStorage.getItem(SITENAMEALIAS + "_exchange")}
                  , {localStorage.getItem(SITENAMEALIAS + "_first_name")}{" "}
                  {localStorage.getItem(SITENAMEALIAS + "_last_name")}
                </h2>
                <p style={styles.welcomeSubtitle}>{this.handleUserStatus()}</p>
              </div>

              {/* Progress Stepper */}
              <div className="gs-progress-track" style={styles.progressTrack}>
                {stepLabels.map((label, idx) => (
                  <React.Fragment key={idx}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        zIndex: 1,
                      }}
                    >
                      <div
                        style={styles.stepCircle(
                          stepDone[idx],
                          activeStep === idx + 1
                        )}
                      >
                        {stepDone[idx] ? (
                          <i
                            className="fas fa-check"
                            style={{ fontSize: "14px" }}
                          ></i>
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span
                        className="gs-step-label"
                        style={styles.stepLabel(
                          stepDone[idx],
                          activeStep === idx + 1
                        )}
                      >
                        {label}
                      </span>
                    </div>
                    {idx < stepLabels.length - 1 && (
                      <div style={styles.connector(stepDone[idx])}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Cards */}
              <div className="gs-cards-row" style={styles.cardsRow}>
                {/* Card 1 - Email */}
                <div style={styles.card(activeStep === 1, steps.step1, false)}>
                  <span style={styles.stepNumber}>Step 1</span>
                  {activeStep === 1 && (
                    <div style={styles.badge}>Action Required</div>
                  )}
                  {steps.step1 && (
                    <div style={styles.completedBadge}>Completed</div>
                  )}
                  <div style={styles.iconArea(activeStep === 1, steps.step1)}>
                    <i
                      className={`fas ${
                        this.state.isEmailConfirmed != "1"
                          ? "fa-envelope"
                          : "fa-envelope-circle-check"
                      }`}
                      style={styles.icon(activeStep === 1, steps.step1)}
                    ></i>
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>
                      {this.state.isEmailConfirmed != "1"
                        ? "Confirm Your Email"
                        : "Email Confirmed"}
                    </h3>
                    <p style={styles.cardDesc}>
                      {this.state.isEmailConfirmed != "1"
                        ? "Your email address is not confirmed yet. Please check your inbox."
                        : "Nice, your email address has been confirmed."}
                    </p>
                    <div style={{ marginTop: "auto" }}>
                      {this.state.isEmailConfirmed != "1" && (
                        <div>
                          {!this.state.isEmailResend &&
                          this.state.permission == "WRITE" ? (
                            <button
                              style={styles.btnPrimary}
                              onClick={this.handleResendEmail}
                            >
                              Re-send Confirmation
                            </button>
                          ) : (
                            <span
                              style={{
                                color: "#10b981",
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              <i
                                className="mdi mdi-email-variant"
                                style={{ marginRight: "4px" }}
                              ></i>
                              Email Sent Successfully
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card 2 - Verification */}
                <div style={styles.card(activeStep === 2, steps.step2, false)}>
                  <span style={styles.stepNumber}>Step 2</span>
                  {activeStep === 2 && (
                    <div style={styles.badge}>Action Required</div>
                  )}
                  {steps.step2 && (
                    <div style={styles.completedBadge}>Completed</div>
                  )}
                  <div style={styles.iconArea(activeStep === 2, steps.step2)}>
                    <i
                      className={`fas ${
                        this.state.isVerificationSubmitted != "1"
                          ? "fa-user"
                          : "fa-user-check"
                      }`}
                      style={styles.icon(activeStep === 2, steps.step2)}
                    ></i>
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>
                      {this.state.isVerificationSubmitted != "1"
                        ? "Business Activation"
                        : "Verification Completed"}
                    </h3>
                    <p style={styles.cardDesc}>
                      {this.state.isVerificationSubmitted != "1"
                        ? "Verify your business to activate live processing."
                        : "Your business verification is complete. You are ready to accept payments."}
                    </p>
                    <div style={{ marginTop: "auto" }}>
                      {this.state.isVerificationSubmitted != "1" ? (
                        this.state.permission == "WRITE" ? (
                          <Link
                            style={styles.btnPrimary}
                            to="/basic-verification"
                          >
                            Activate
                          </Link>
                        ) : (
                          <button
                            type="button"
                            style={styles.btnDisabled}
                            onClick={() =>
                              showToast(
                                "error",
                                "You need write access to perform this operation"
                              )
                            }
                            disabled
                          >
                            Activate
                          </button>
                        )
                      ) : this.state.permission == "WRITE" ? (
                        <Link
                          style={styles.btnOutline}
                          to="/basic-verification"
                        >
                          View
                        </Link>
                      ) : (
                        <button
                          type="button"
                          style={styles.btnDisabled}
                          onClick={() =>
                            showToast(
                              "error",
                              "You need write access to perform this operation"
                            )
                          }
                          disabled
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card 3 - Accept Payment */}
                <div
                  data-tour="accept-payment"
                  style={styles.card(
                    activeStep === 3,
                    steps.step3,
                    this.state.isVerificationSubmitted != "1"
                  )}
                >
                  <span style={styles.stepNumber}>Step 3</span>
                  {activeStep === 3 && (
                    <div style={styles.badge}>Action Required</div>
                  )}
                  {steps.step3 && (
                    <div style={styles.completedBadge}>Completed</div>
                  )}
                  <div style={styles.iconArea(activeStep === 3, steps.step3)}>
                    <i
                      className="fas fa-bolt"
                      style={styles.icon(activeStep === 3, steps.step3)}
                    ></i>
                  </div>
                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>Accept Payment</h3>
                    <p style={styles.cardDesc}>
                      {this.state.isCryptoAddressAdded != "1"
                        ? "Create your first payment link and start accepting payments."
                        : "Crypto addresses added. You can also add or update other crypto addresses."}
                    </p>
                    <div style={{ marginTop: "auto" }}>
                      {this.state.isVerificationSubmitted != "1" ? (
                        <button
                          type="button"
                          style={styles.btnDisabled}
                          onClick={() =>
                            showToast(
                              "error",
                              "Please complete Business Activation (Step 2) first"
                            )
                          }
                          disabled
                        >
                          Create
                        </button>
                      ) : this.state.permission == "WRITE" ? (
                        <Link style={styles.btnPrimary} to="/payment-button">
                          Create
                        </Link>
                      ) : (
                        <button
                          type="button"
                          style={styles.btnDisabled}
                          onClick={() =>
                            showToast(
                              "error",
                              "You need write access to perform this operation"
                            )
                          }
                          disabled
                        >
                          Create
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* All Complete Message */}
              {allComplete && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "32px",
                    padding: "16px 24px",
                    background:
                      "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.03))",
                    borderRadius: "12px",
                    border: "1px solid rgba(16,185,129,0.2)",
                  }}
                >
                  <i
                    className="fas fa-circle-check"
                    style={{
                      color: "#10b981",
                      fontSize: "20px",
                      marginRight: "8px",
                    }}
                  ></i>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#10b981",
                    }}
                  >
                    All steps completed! Your account is fully set up.
                  </span>
                </div>
              )}

              {/* Card Applications Section */}
              {this.state.cardApplications && this.state.cardApplications.length > 0 && (
                <div style={{ marginTop: "36px", background: "var(--body-color-less)", padding: "24px", borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid var(--body-color-less2)" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "16px", color: "var(--text-color)" }}>Card Applications</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {this.state.cardApplications.map((app, index) => {
                      const normalized = app.cardProcessorName ? app.cardProcessorName.replace(/\s+/g, '').toLowerCase() : '';
                      const map = {
                        stripe: "PayBito Apex",
                        paypal: "PayBito Titan",
                        kurvpay: "PayBito Nexus",
                        netbilling: "PayBito Vertex",
                        hostmerchantservices: "PayBito Nova",
                        cardflo: "PayBito Sovereign",
                        nmi: "PayBito Zenith",
                        durangomerchantservices: "PayBito Quantum"
                      };
                      const displayName = map[normalized] ? `${map[normalized]} Application` : `${app.cardProcessorName || "Card"} Application`;
                      
                      let statusColor = "var(--text-color)";
                      if (app.applicationStatus === "Approved") statusColor = "#10b981";
                      if (app.applicationStatus === "Pending" || app.applicationStatus === "Submitted") statusColor = "#f59e0b";
                      if (app.applicationStatus === "Declined") statusColor = "#ef4444";

                      return (
                        <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "var(--body-color)", borderRadius: "8px", border: "1px solid var(--body-color-less2)" }}>
                          <span style={{ fontWeight: "600", fontSize: "14px", color: "var(--text-color)" }}>{displayName}</span>
                          <span style={{ fontWeight: "700", fontSize: "12px", padding: "4px 12px", borderRadius: "20px", background: `color-mix(in srgb, ${statusColor}, transparent 90%)`, color: statusColor }}>
                            {app.applicationStatus}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <Footer />
          </div>
          <Loader show={this.state.showLoader} />
        </div>
      </Fragment>
    );
  }
}

export default GetStarted;
